import * as React from 'react';
import { cn } from '../../lib/utils';
import { Button } from '../Button';
import { Input } from '../Input';
import { Textarea } from '../Textarea';
import { inputGroupAddonVariants, inputGroupButtonVariants } from './variants';
import type {
  InputGroupProps,
  InputGroupAddonProps,
  InputGroupButtonProps,
  InputGroupTextProps,
  InputGroupInputProps,
  InputGroupTextareaProps,
} from './types';

const InputGroup = React.forwardRef<HTMLDivElement, InputGroupProps>(
  ({ className, ...props }, ref) => {
    return (
      <div
        ref={ref}
        data-slot='input-group'
        role='group'
        className={cn(
          //Typography
          'eye:text-grey-120',
          // Layout and surface
          'eye:group/input-group eye:relative eye:flex eye:w-full eye:min-w-0 eye:items-center eye:rounded-md eye:border eye:border-grey-100 eye:bg-grey-5 eye:outline-offset-3 eye:outline-black eye:transition-[border-color,outline-color,box-shadow] eye:has-disabled:cursor-not-allowed eye:has-disabled:bg-grey-disabled',
          // Sizing
          'eye:min-h-48 eye:has-[>textarea]:h-auto',
          // Alignment variants
          'eye:has-[>[data-align=inline-start]]:[&>input]:pl-8',
          'eye:has-[>[data-align=inline-end]]:[&>input]:pr-8',
          'eye:has-[>[data-align=block-start]]:h-auto eye:has-[>[data-align=block-start]]:flex-col eye:has-[>[data-align=block-start]]:[&>input]:pb-12',
          'eye:has-[>[data-align=block-end]]:h-auto eye:has-[>[data-align=block-end]]:flex-col eye:has-[>[data-align=block-end]]:[&>input]:pt-12',
          // Interactive states
          'eye:focus-within:border-black eye:focus-within:outline-2 eye:hover:border-black eye:has-disabled:hover:border-grey-100!',
          // Validation state
          'eye:has-aria-invalid:border-red-error',
          className,
        )}
        {...props}
      />
    );
  },
);

InputGroup.displayName = 'InputGroup';

function InputGroupAddon({
  className,
  align = 'inline-start',
  ...props
}: InputGroupAddonProps) {
  return (
    <div
      role='group'
      data-slot='input-group-addon'
      data-align={align}
      className={cn(inputGroupAddonVariants({ align }), className)}
      {...props}
    />
  );
}

const InputGroupButton = React.forwardRef<
  HTMLButtonElement,
  InputGroupButtonProps
>(
  (
    { className, type = 'button', variant = 'ghost', size = 'xs', ...props },
    ref,
  ) => {
    return (
      <Button
        ref={ref}
        type={type}
        data-size={size}
        variant={variant}
        size={size}
        className={cn(inputGroupButtonVariants({ size }), className)}
        {...props}
      />
    );
  },
);

InputGroupButton.displayName = 'InputGroupButton';

function InputGroupText({ className, ...props }: InputGroupTextProps) {
  return (
    <span
      className={cn(
        'eye:flex eye:items-center eye:gap-8 eye:text-14 eye:text-grey-120 eye:[&_svg]:pointer-events-none eye:[&_svg:not([class*=size-])]:size-16',
        className,
      )}
      {...props}
    />
  );
}

const InputGroupInput = React.forwardRef<
  HTMLInputElement,
  InputGroupInputProps
>(({ className, ...props }, ref) => {
  return (
    <Input
      ref={ref}
      data-slot='input-group-control'
      className={cn(
        // Layout and spacing
        'eye:h-full eye:min-h-48 eye:w-full eye:flex-1 eye:truncate eye:pr-8 eye:pl-16',
        // Surface and typography
        'eye:rounded-none eye:border-0 eye:bg-transparent eye:text-16 eye:shadow-none eye:disabled:bg-transparent',
        // Focus state
        'eye:focus:ring-0 eye:focus:outline-none eye:focus-visible:ring-0 eye:focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
});

InputGroupInput.displayName = 'InputGroupInput';

const InputGroupTextarea = React.forwardRef<
  HTMLTextAreaElement,
  InputGroupTextareaProps
>(({ className, ...props }, ref) => {
  return (
    <Textarea
      ref={ref}
      data-slot='input-group-control'
      className={cn(
        // Layout and spacing
        'eye:py-3 eye:flex-1 eye:resize-none',
        // Surface
        'eye:rounded-none eye:border-0 eye:bg-transparent eye:shadow-none eye:disabled:bg-transparent',
        // Focus state
        'eye:focus:outline-none eye:focus-visible:outline-none',
        className,
      )}
      {...props}
    />
  );
});

InputGroupTextarea.displayName = 'InputGroupTextarea';

export {
  InputGroup,
  InputGroupAddon,
  InputGroupButton,
  InputGroupText,
  InputGroupInput,
  InputGroupTextarea,
};
